/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * RecipeDraft → the body POST /api/recipes expects.
 *
 * ── WHY IT IS SHARED ────────────────────────────────────────────────────────
 * The editor already does this, via `fromDraft()` into a form and `toBody()`
 * back out again — a round trip through string-typed form state that exists
 * because a human is going to edit the values in between.
 *
 * The first-run wizard saves generated drafts without a human in the middle, so
 * it needs the same mapping without the form. Writing that inline would have
 * created a second, quietly diverging definition of how a draft becomes a
 * recipe — and the failure mode of divergence here is silent: a field the
 * wizard forgets to map is simply absent from every recipe it creates, and
 * nothing errors.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * `matchConfidence`, `foodId` and `matchVia` are dropped, exactly as the editor
 * drops them. The server re-matches ingredients against the food catalog on
 * write; sending the model's guesses would let a low-confidence match masquerade
 * as an authoritative one.
 */

import type { RecipeDraft } from "../api/types.ts";

export interface DraftSource {
  /** "ai" | "url" | "scan" | "manual" */
  source: string;
  sourceUrl?: string;
}

export function draftToBody(d: RecipeDraft, origin: DraftSource): Record<string, unknown> {
  return {
    title: d.title.trim(),
    description: d.description?.trim() || undefined,
    servingsBase: d.servingsBase ?? 4,
    prepMinutes: d.prepMinutes,
    cookMinutes: d.cookMinutes,
    cuisine: d.cuisine?.trim() || undefined,
    course: d.course?.trim() || undefined,
    dietTags: d.dietTags ?? [],
    source: origin.source,
    sourceUrl: origin.sourceUrl,
    ingredients: (d.ingredients ?? [])
      .filter((i) => i.displayName?.trim())
      .map((i) => ({
        displayName: i.displayName.trim(),
        quantity: i.quantity,
        unit: i.unit || undefined,
        prepNote: i.prepNote || undefined,
        isOptional: !!i.isOptional,
        scaleMode: i.scaleMode ?? "linear",
      })),
    steps: (d.steps ?? [])
      .filter((s) => s.text?.trim())
      .map((s) => ({ text: s.text.trim(), durationMinutes: s.durationMinutes })),
    // Carried through when the draft has one, unlike the match fields above.
    // A URL import off a site that publishes schema.org nutrition, or a
    // generated recipe whose model volunteered a panel, arrives with real
    // figures — and dropping them here would mean the server paid Opus to
    // re-derive numbers it had already been handed for free.
    //
    // Preserved, not trusted: the server re-normalises and clamps it on write,
    // exactly as it does for a panel it estimated itself.
    nutrition: d.nutrition ?? undefined,
  };
}
